import React, { useEffect, useState, useRef } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./OurWork.css";

export default function OurWork() {
  const [cards, setCards] = useState([]);
  const [displayedCards, setDisplayedCards] = useState([]);
  const [loadMoreCount, setLoadMoreCount] = useState(6);
  const [sortOption, setSortOption] = useState("newest");
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const dropdownRef = useRef();
  const navigate = useNavigate();

  const sortOptions = [
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
    { value: "A-Z", label: "A-Z" },
    { value: "Z-A", label: "Z-A" },
    { value: "recent-month", label: "Last Month" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch cards from Firestore
  useEffect(() => {
    const q = query(collection(db, "workCards"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const cardsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        active: doc.data().active === true,
      }));
      setCards(cardsData);
    });
    return () => unsubscribe();
  }, []);

  // Filter, search, sort, paginate
  useEffect(() => {
    let filtered = cards.filter((c) => c.active);

    if (search.trim() !== "") {
      filtered = filtered.filter(
        (c) =>
          c.title?.toLowerCase().includes(search.toLowerCase()) ||
          c.summary?.toLowerCase().includes(search.toLowerCase()) ||
          c.description?.toLowerCase().includes(search.toLowerCase())
      );
    }

    switch (sortOption) {
      case "newest":
        filtered.sort(
          (a, b) =>
            new Date(b.createdAt?.seconds * 1000 || 0) -
            new Date(a.createdAt?.seconds * 1000 || 0)
        );
        break;
      case "oldest":
        filtered.sort(
          (a, b) =>
            new Date(a.createdAt?.seconds * 1000 || 0) -
            new Date(b.createdAt?.seconds * 1000 || 0)
        );
        break;
      case "A-Z":
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case "Z-A":
        filtered.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case "recent-month":
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        filtered = filtered.filter(
          (c) => new Date(c.createdAt?.seconds * 1000 || 0) >= oneMonthAgo
        );
        break;
      default:
        break;
    }

    setDisplayedCards(filtered.slice(0, loadMoreCount));
  }, [cards, search, sortOption, loadMoreCount]);

  return (
    <div className="our-work-container">
      <h1 className="projects-title">Our Work</h1>
      <p className="projects-subtitle">
        Explore our projects. Search by title, summary, or description below.
      </p>

      {/* Search & Sort */}
      <div className="work-filters">
        <input
          type="text"
          placeholder="Search projects..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />

        <div className="custom-dropdown" ref={dropdownRef}>
          <div
            className="dropdown-selected"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {sortOptions.find((o) => o.value === sortOption)?.label}
            <span className={`arrow ${dropdownOpen ? "open" : ""}`} />
          </div>

          {dropdownOpen && (
            <div className="dropdown-options">
              {sortOptions.map((option) => (
                <div
                  key={option.value}
                  className="dropdown-option"
                  onClick={() => {
                    setSortOption(option.value);
                    setDropdownOpen(false);
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Projects Grid */}
      <div className="projects-grid">
        {displayedCards.map((card) => (
          <div
            key={card.id}
            className="project-card fade-in"
            onClick={() => navigate(`/work/${card.id}`)}
          >
            <div className="project-image-wrapper">
              <img src={card.image} alt={card.title} loading="lazy" />
              <div className="overlay">
                <h3>{card.title}</h3>
                {card.summary && <p className="summary">{card.summary}</p>}
                <p>{card.description}</p>
              </div>
            </div>

            <div className="project-info">
              <h3>{card.title}</h3>
              {card.summary && <p className="summary">{card.summary}</p>}
              <p>{card.description}</p>
              {card.type && <span className="type">Type: {card.type}</span>}
              {card.material && <span className="material">Material: {card.material}</span>}
              {card.color && <span className="color">Color: {card.color}</span>}
              {card.price && <span className="price">€{card.price}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Load More */}
      {displayedCards.length < cards.length && (
        <div className="load-more-container">
          <button onClick={() => setLoadMoreCount(loadMoreCount + 6)}>
            Load More
          </button>
        </div>
      )}

      {displayedCards.length === 0 && (
        <p className="no-projects">No projects found.</p>
      )}
    </div>
  );
}
